import { FormControl, FormHelperText, FormLabel, GridLegacy as Grid, Stack } from "@mui/material";
import { defer, entries, isEmpty, omit } from "lodash-es";
import React, { useCallback } from "react";
import { Controller, FieldValues, useFormContext, useFormState, useWatch, ValidationRule } from "react-hook-form";

import { useDeepEqual } from "../../hooks/useDeepEqual";
import {
    CA_POSTCODE_REGEX,
    CA_PROVINCE_OPTIONS,
    isPostalCodeRequired,
    isStateRequired,
    US_STATE_OPTIONS,
    US_ZIPCODE_REGEX,
} from "../../utils/addressUtils";
import { assertNever } from "../../utils/assertNever";
import { SENSITIVE_BY_CLASS_NAME } from "../../utils/highlightUtils";
import { CA_PROVINCES_REGEX, US_STATES_REGEX } from "../../utils/states";
import { createNoExclusivelyNumericValidator, createNonEmptyStringValidator } from "../../utils/validators";
import { IAddressComponents } from "./AddressAutocomplete/autocomplete-utils";
import {
    GoogleAddressAutocomplete,
    IGoogleAddressAutocompleteProps,
} from "./AddressAutocomplete/GoogleAddressAutocomplete";
import { ControllerCountrySelect } from "./ControllerCountrySelect";
import { ControllerStringOptionSelect } from "./ControllerStringOptionSelect";
import { ControllerTextField } from "./ControllerTextField";
import { FieldPathOfType, IControllerProps } from "./formTypes";
import { getMaybeErrorMessage } from "./getMaybeErrorMessage";

export type IAddressFieldPaths<TFieldValues extends FieldValues = FieldValues> = {
    [K in keyof IAddressComponents]-?: FieldPathOfType<TFieldValues, IAddressComponents[K]>;
};

export type FormSpreadAddressInputLabelVariant = "standard" | "small";

interface IProps<TFieldValues extends FieldValues, TAddressFields extends IAddressFieldPaths<TFieldValues>> {
    addressFields: TAddressFields;
    label?: string;
    labelVariant?: FormSpreadAddressInputLabelVariant;
    description?: string;
    disableCountry?: boolean;
    defaultValue?: Partial<IAddressComponents>;
}

export function FormSpreadAddressInput<
    TFieldValues extends FieldValues = FieldValues,
    TAddressFields extends IAddressFieldPaths<TFieldValues> = IAddressFieldPaths<TFieldValues>,
>({
    addressFields: rawAddressFields,
    description,
    label,
    labelVariant = "standard",
    disableCountry,
    defaultValue,
}: IProps<TFieldValues, TAddressFields>): React.ReactElement | null {
    const addressFields = useDeepEqual(rawAddressFields);
    const {
        street: streetField,
        street2: street2Field,
        city: cityField,
        state: stateField,
        zipCode: zipCodeField,
        country: countryField,
    } = addressFields;

    const { setValue, trigger } = useFormContext();
    const { errors } = useFormState({ name: Object.values(addressFields) });

    const streetErrorMessage = getMaybeErrorMessage(errors, streetField);
    const street2ErrorMessage = getMaybeErrorMessage(errors, street2Field);
    const cityErrorMessage = getMaybeErrorMessage(errors, cityField);
    const stateErrorMessage = getMaybeErrorMessage(errors, stateField);
    const zipCodeErrorMessage = getMaybeErrorMessage(errors, zipCodeField);
    const countryErrorMessage = getMaybeErrorMessage(errors, countryField);

    const countryCode: string = useWatch({ name: countryField });
    const handleChangeCountry = useCallback((): void => {
        setValue(stateField, "" as never);
        setValue(zipCodeField, "" as never);
    }, [stateField, zipCodeField, setValue]);

    const handleSelectAutocomplete = useCallback(
        (address: IAddressComponents): void => {
            const addressToModify = disableCountry ? omit(address, "country") : address;

            const modifiedFields: string[] = [];
            entries(addressToModify).forEach(([key, value]) => {
                const addressKey = key as keyof IAddressComponents;
                const field = addressFields[addressKey];

                setValue(field, value as never);
                modifiedFields.push(field);
            });
            // Run validation of autocomplete fields after a render loop so that the rules are up-to-date
            defer(() => trigger(modifiedFields));
        },
        [addressFields, disableCountry, setValue, trigger],
    );

    function renderState(definedStateField: string) {
        if (countryCode === "US") {
            return (
                <ControllerStringOptionSelect
                    key={countryCode}
                    name={definedStateField}
                    controllerProps={{
                        rules: {
                            required: "State required",
                            pattern: { value: US_STATES_REGEX, message: "Invalid state" },
                        },
                        defaultValue: defaultValue?.state,
                    }}
                    required={true}
                    error={stateErrorMessage != null}
                    label="State"
                    placeholder="Select a state"
                    options={US_STATE_OPTIONS}
                />
            );
        } else if (countryCode === "CA") {
            return (
                <ControllerStringOptionSelect
                    key={countryCode}
                    name={definedStateField}
                    controllerProps={{
                        rules: {
                            required: "Province required",
                            pattern: { value: CA_PROVINCES_REGEX, message: "Invalid province" },
                        },
                        defaultValue: defaultValue?.state,
                    }}
                    required={true}
                    error={stateErrorMessage != null}
                    label="Province"
                    placeholder="Select a province"
                    options={CA_PROVINCE_OPTIONS}
                />
            );
        }

        const isRequired = isStateRequired(countryCode);
        return (
            <ControllerTextField
                key={countryCode}
                name={definedStateField}
                controllerProps={{
                    rules: {
                        required: isRequired && "State/province required",
                        // Clear existing pattern rule if swapping from US or CA
                        pattern: undefined,
                        validate: createNoExclusivelyNumericValidator("State/Province must be valid", !isRequired),
                    },
                    defaultValue: defaultValue?.state,
                }}
                fullWidth={true}
                label="State / Province"
                error={stateErrorMessage != null}
                inputProps={{ autoComplete: "off" }}
            />
        );
    }

    function maybeRenderCityLineError() {
        if (cityErrorMessage != null) {
            return <FormHelperText error={true}>{cityErrorMessage}</FormHelperText>;
        } else if (stateErrorMessage != null) {
            return <FormHelperText error={true}>{stateErrorMessage}</FormHelperText>;
        } else if (zipCodeErrorMessage != null) {
            return <FormHelperText error={true}>{zipCodeErrorMessage}</FormHelperText>;
        }

        return null;
    }

    const isZipRequired = isPostalCodeRequired(countryCode);

    return (
        <FormControl variant="standard" fullWidth={true} required={true}>
            {!isEmpty(label) && (
                <FormLabel
                    sx={(theme) => ({
                        ...(labelVariant === "standard"
                            ? {}
                            : labelVariant === "small"
                              ? theme.typography.label1
                              : assertNever(labelVariant)),
                    })}
                >
                    {label}
                </FormLabel>
            )}
            <FormHelperText error={false} sx={{ mt: 0, mb: 0.5 }}>
                {description}
            </FormHelperText>
            <Stack spacing={3} mt={2}>
                <ControllerStreetAddressAutocomplete
                    name={streetField}
                    controllerProps={{
                        rules: {
                            required: true,
                            validate: createNoExclusivelyNumericValidator("Street must be valid"),
                        },
                        defaultValue: defaultValue?.street as never,
                    }}
                    label="Street Address"
                    originCountryCode={countryCode}
                    restrictToCountry={disableCountry}
                    error={streetErrorMessage != null}
                    helperText={streetErrorMessage}
                    onSelect={handleSelectAutocomplete}
                />
                <ControllerTextField
                    name={street2Field}
                    controllerProps={{ defaultValue: defaultValue?.street2 as never }}
                    fullWidth={true}
                    label="Street Address 2"
                    error={street2ErrorMessage != null}
                    helperText={street2ErrorMessage}
                    inputProps={{ autoComplete: "off", ...SENSITIVE_BY_CLASS_NAME }}
                />
                <Stack>
                    <div>
                        <Grid container={true} spacing={2}>
                            <Grid item={true} xs={12} md={6}>
                                <ControllerTextField
                                    name={cityField}
                                    controllerProps={{
                                        rules: {
                                            required: "City required",
                                            validate: createNoExclusivelyNumericValidator("City must be valid"),
                                        },
                                        defaultValue: defaultValue?.city as never,
                                    }}
                                    fullWidth={true}
                                    label="City"
                                    error={cityErrorMessage != null}
                                    inputProps={{ autoComplete: "off" }}
                                />
                            </Grid>
                            <Grid item={true} xs={8} md={4}>
                                {renderState(stateField)}
                            </Grid>
                            <Grid item={true} xs={4} md={2}>
                                <ControllerTextField
                                    name={zipCodeField}
                                    controllerProps={{
                                        rules: {
                                            required: isZipRequired && "ZIP code required",
                                            pattern:
                                                countryCode === "US"
                                                    ? { value: US_ZIPCODE_REGEX, message: "Invalid ZIP code" }
                                                    : countryCode === "CA"
                                                      ? { value: CA_POSTCODE_REGEX, message: "Invalid postal code" }
                                                      : undefined,
                                            validate: isZipRequired
                                                ? createNonEmptyStringValidator("ZIP code cannot be empty")
                                                : undefined,
                                            maxLength: MAX_LENGTH_POSTAL_CODE,
                                        },
                                        defaultValue: defaultValue?.zipCode as never,
                                    }}
                                    fullWidth={true}
                                    label="ZIP Code"
                                    error={zipCodeErrorMessage != null}
                                    inputProps={{ autoComplete: "off", ...SENSITIVE_BY_CLASS_NAME }}
                                />
                            </Grid>
                        </Grid>
                    </div>
                    {maybeRenderCityLineError()}
                </Stack>
                <ControllerCountrySelect
                    name={countryField}
                    controllerProps={{
                        rules: { required: true, deps: [stateField, zipCodeField] },
                        defaultValue: defaultValue?.country as never,
                    }}
                    disabled={disableCountry}
                    label="Country"
                    error={countryErrorMessage != null}
                    helperText={countryErrorMessage}
                    onChange={handleChangeCountry}
                />
            </Stack>
        </FormControl>
    );
}

interface IControllerStreetAddressAutocompleteProps<
    TFieldValues extends FieldValues,
    TName extends FieldPathOfType<TFieldValues, string>,
> extends Omit<IGoogleAddressAutocompleteProps, "inputValue" | "onChange" | "onBlur"> {
    name: TName;
    controllerProps?: IControllerProps<TFieldValues, TName, string>;
}

function ControllerStreetAddressAutocomplete<
    TFieldValues extends FieldValues,
    TName extends FieldPathOfType<TFieldValues, string>,
>({
    name,
    controllerProps,
    ...googleAddressAutocompleteProps
}: IControllerStreetAddressAutocompleteProps<TFieldValues, TName>): React.ReactElement | null {
    return (
        <Controller
            {...controllerProps}
            // Must set default value or things don't play nicely with MUI.
            defaultValue={controllerProps?.defaultValue ?? ("" as never)}
            name={name}
            render={({ field: { ref, value, onChange, onBlur } }) => (
                <GoogleAddressAutocomplete
                    {...googleAddressAutocompleteProps}
                    ref={ref}
                    inputValue={value}
                    onChangeInputValue={onChange}
                    onBlur={onBlur}
                />
            )}
        />
    );
}

// limitation from Currency Cloud
const MAX_POSTAL_CODE_CHARACTER_COUNT = 12;
const MAX_LENGTH_POSTAL_CODE: ValidationRule<number> = {
    value: MAX_POSTAL_CODE_CHARACTER_COUNT,
    message: `ZIP code cannot exceed ${MAX_POSTAL_CODE_CHARACTER_COUNT} characters`,
};
